import type { InventoryReport, InventoryRiskRow } from '../../types/reports';
import type { Segment } from '../reportsView';
import { intLabel, moneyLabel, plural, strong, text, type RowView, type SectionView, type StatView } from './shared';

/**
 * P-084 · R6 «¿Cómo está mi inventario?» — presentación pura. La existencia es la de AHORA; el movimiento, el del
 * periodo (se dice). Sin mínimos configurados no hay «bajo mínimo»: se avisa por ritmo de consumo con la definición
 * única del Inicio. Nada sin costo se suma como $0.
 */
export interface InventoryView {
  say: Segment[];
  stats: StatView[];
  attention: SectionView;
  detail: SectionView[];
  notes: string[];
  empty: { title: string; description: string } | null;
}

export const INVENTORY_NOW_NOTE = 'La existencia es la de ahora; el consumo, las mermas y las compras son del periodo.';
export const INVENTORY_NO_MINIMUMS_NOTE = 'Todavía no tienes mínimos por insumo; te aviso por ritmo de consumo. Los productos con costo aproximado o sin receta no descuentan inventario.';
export const INVENTORY_REVERSALS_NOTE = 'Una venta anulada en el periodo resta su consumo, aunque la venta haya sido anterior.';
export const INVENTORY_EMPTY_TITLE = 'Todavía no hay existencias registradas.';
export const INVENTORY_EMPTY_TEXT = 'Cuando registres compras o un conteo inicial, aquí verás qué se está acabando y qué se mueve.';

const STATE_TEXT: Record<InventoryRiskRow['state'], (row: InventoryRiskRow) => string> = {
  NEGATIVE_STOCK: () => 'Menos de cero: revisa si falta una compra o sobra un descuento.',
  OUT_OF_STOCK: () => 'Agotado, y lo estabas usando esta semana.',
  ESTIMATE: (row) => `A este ritmo te dura ${row.daysLeft === 0 ? 'menos de 1 día' : `${row.daysLeft} ${plural(row.daysLeft ?? 0, 'día', 'días')}`}${row.estimateQuality === 'PRELIMINARY' ? ' (estimación preliminar: poca historia)' : ''}.`,
};
const STATE_BADGE: Record<InventoryRiskRow['state'], { text: string; tone: 'danger' | 'warning' | 'info' }> = {
  NEGATIVE_STOCK: { text: 'En negativo', tone: 'danger' },
  OUT_OF_STOCK: { text: 'Agotado', tone: 'danger' },
  ESTIMATE: { text: 'Se acaba', tone: 'warning' },
};

export function inventoryReportView(report: InventoryReport): InventoryView {
  const { riskCounts, history, value } = report;
  const notes = [INVENTORY_NOW_NOTE, INVENTORY_NO_MINIMUMS_NOTE];
  if (value.note) notes.push(value.note);
  // R2-06: «sin existencias» solo cuando no hay NADA: ni riesgos (un negativo o un agotado no son existencia positiva), ni saldos, ni movimiento.
  if (riskCounts.total === 0 && value.items === 0 && report.consumption.rows.length === 0 && report.purchases.rows === 0 && report.waste.rows === 0) {
    return { say: [text(INVENTORY_EMPTY_TITLE)], stats: statsOf(report), attention: { key: 'attention', title: 'Requiere atención', rows: [], empty: null, note: null, more: null }, detail: [], notes, empty: { title: INVENTORY_EMPTY_TITLE, description: INVENTORY_EMPTY_TEXT } };
  }

  const say: Segment[] = [];
  if (riskCounts.total > 0) {
    const parts: string[] = [];
    if (riskCounts.negative > 0) parts.push(`${riskCounts.negative} ${plural(riskCounts.negative, 'está', 'están')} en negativo`);
    if (riskCounts.outOfStock > 0) parts.push(`${riskCounts.outOfStock} se ${plural(riskCounts.outOfStock, 'agotó', 'agotaron')}`);
    if (riskCounts.estimate > 0) parts.push(`${riskCounts.estimate} se ${plural(riskCounts.estimate, 'acaba', 'acaban')} pronto`);
    say.push(strong(`${riskCounts.total} ${plural(riskCounts.total, 'insumo necesita', 'insumos necesitan')} tu atención`), text(`: ${joinHuman(parts)}.`));
  } else if (history.anyStoreWithHistory) {
    say.push(text('No detectamos insumos por acabarse donde hay historia suficiente, y nada está en negativo ni agotado.'));
  } else {
    say.push(text('Todavía no tengo suficiente historia de consumo para estimar qué se acaba; nada está en negativo ni agotado.'));
  }
  if (history.storesWithoutHistory.length > 0 && history.anyStoreWithHistory) {
    say.push(text(` En ${joinHuman(history.storesWithoutHistory)} aún no hay ${history.minDays} días de consumo para estimar.`));
  }

  const attention: SectionView = {
    key: 'attention',
    title: 'Requiere atención',
    rows: report.risks.map((risk) => ({
      key: `${risk.storeId}:${risk.ingredientId}`,
      title: risk.ingredientName,
      subtitle: STATE_TEXT[risk.state](risk),
      meta: [risk.storeName],
      value: `${quantityLabel(risk.quantityOnHand)} ${risk.unitSymbol}`.trim(),
      valueTone: risk.state === 'NEGATIVE_STOCK' ? 'danger' : 'default',
      badge: STATE_BADGE[risk.state],
      figures: [],
      destination: 'inventory/stock',
    })),
    empty: riskCounts.total === 0 ? 'Nada que atender ahora.' : null,
    note: null,
    more: report.moreRisks > 0 ? `${report.moreRisks} ${plural(report.moreRisks, 'insumo más', 'insumos más')} por revisar en Inventario.` : null,
  };

  const consumptionRows: RowView[] = report.consumption.rows.map((row) => ({
    key: `${row.storeId}:${row.ingredientId}`,
    title: row.ingredientName,
    subtitle: Number(row.quantity) < 0
      ? 'Más reversas que salidas en el periodo: la salida original fue antes.'
      : row.costCoverage === 'none' ? 'Sin costo registrado: no se puede valuar.' : row.costCoverage === 'partial' ? `Parte sin costo (${quantityLabel(row.uncostedQuantity)} ${row.unitSymbol}).` : null,
    meta: [row.storeName],
    value: `${quantityLabel(row.quantity)} ${row.unitSymbol}`.trim(),
    valueTone: 'default',
    badge: null,
    figures: row.knownCost === null ? [] : [{ label: 'Costo conocido', value: moneyLabel(row.knownCost) }],
    destination: null,
  }));
  const idleRows: RowView[] = report.idle.rows.map((row) => ({
    key: `${row.storeId}:${row.ingredientId}`, title: row.ingredientName, subtitle: null, meta: [row.storeName],
    value: `${quantityLabel(row.quantityOnHand)} ${row.unitSymbol}`.trim(), valueTone: 'muted', badge: null, figures: [], destination: null,
  }));

  const detail: SectionView[] = [
    { key: 'consumption', title: 'Lo que más se movió', rows: consumptionRows, empty: 'Ningún insumo se descontó por ventas en el periodo.', note: INVENTORY_REVERSALS_NOTE, more: report.consumption.moreCount > 0 ? `${report.consumption.moreCount} ${plural(report.consumption.moreCount, 'insumo más', 'insumos más')}.` : null },
    {
      key: 'flows', title: 'Mermas y compras del periodo',
      rows: [
        { key: 'waste', title: 'Mermas', subtitle: report.waste.rows === 0 ? 'Ninguna registrada.' : `${intLabel(report.waste.rows)} ${plural(report.waste.rows, 'registro', 'registros')}${report.waste.uncostedRows > 0 ? ` · ${report.waste.uncostedRows} sin costo` : ''}`, meta: [], value: report.waste.rows === 0 ? null : moneyLabel(report.waste.knownCost), valueTone: 'default', badge: null, figures: [], destination: 'waste' },
        { key: 'purchases', title: 'Entró por compras', subtitle: report.purchases.rows === 0 ? 'Ninguna compra descontada al inventario.' : `${intLabel(report.purchases.rows)} ${plural(report.purchases.rows, 'renglón de compra', 'renglones de compra')}${report.purchases.uncostedRows > 0 ? ` · ${report.purchases.uncostedRows} sin costo` : ''}`, meta: [], value: report.purchases.rows === 0 ? null : moneyLabel(report.purchases.knownCost), valueTone: 'default', badge: null, figures: [], destination: 'purchases' },
      ],
      empty: null, note: null, more: null,
    },
    {
      key: 'idle',
      title: 'Sin movimiento en el periodo',
      rows: idleRows,
      empty: report.idle.applies ? 'Todo lo que tiene existencia se movió en el periodo.' : `Solo se afirma con un periodo de ${report.idle.minDays} días o más.`,
      note: report.idle.applies ? 'Insumos que hoy tienen existencia y no registraron ningún movimiento en el periodo.' : null,
      more: report.idle.count > report.idle.rows.length ? `${report.idle.count - report.idle.rows.length} ${plural(report.idle.count - report.idle.rows.length, 'insumo más', 'insumos más')}.` : null,
    },
  ];

  return { say, stats: statsOf(report), attention, detail, notes, empty: null };
}

function statsOf(report: InventoryReport): StatView[] {
  const { riskCounts, value } = report;
  return [
    { key: 'estimate', label: 'Por acabarse', value: intLabel(riskCounts.estimate), hint: report.history.anyStoreWithHistory ? null : 'sin historia para estimar', tone: riskCounts.estimate > 0 ? 'warning' : 'default' },
    { key: 'out', label: 'Agotados', value: intLabel(riskCounts.outOfStock), hint: null, tone: riskCounts.outOfStock > 0 ? 'danger' : 'default' },
    { key: 'negative', label: 'En negativo', value: intLabel(riskCounts.negative), hint: riskCounts.negative > 0 ? 'problema de captura' : null, tone: riskCounts.negative > 0 ? 'danger' : 'default' },
    { key: 'value', label: 'Valor con costo', value: moneyLabel(value.knownAmount), hint: value.state === 'NOT_COMPUTABLE' ? 'ventas sin descontar' : value.itemsWithoutCost > 0 ? `${intLabel(value.itemsWithoutCost)} ${plural(value.itemsWithoutCost, 'insumo sin costo', 'insumos sin costo')}` : `${intLabel(value.items)} ${plural(value.items, 'insumo', 'insumos')}`, tone: 'default' },
  ];
}

function quantityLabel(value: string): string {
  return Number(value).toLocaleString('es-MX', { maximumFractionDigits: 3 });
}

function joinHuman(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} y ${parts[parts.length - 1]}`;
}
